import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Option {
  id: string;
  label: string;
  floor_plan_id?: string;
  floor_plan_name?: string;
}

interface CircuitMultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (ids: string[]) => void | Promise<void>;
  onSelectionChange?: (ids: string[]) => void; // Immediate callback when items are checked/unchecked
  placeholder?: string;
  className?: string;
  currentFloorPlanId?: string;
}

export const CircuitMultiSelect: React.FC<CircuitMultiSelectProps> = ({
  options,
  selected,
  onChange,
  onSelectionChange,
  placeholder = 'Select loads',
  className,
  currentFloorPlanId,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string[]>(selected);
  const pendingChangesRef = useRef<string[] | null>(null);

  // Sync internal state when external changes
  useEffect(() => {
    setInternal(selected);
  }, [selected]);

  const applyPending = () => {
    if (pendingChangesRef.current) {
      const next = pendingChangesRef.current;
      pendingChangesRef.current = null;
      if (JSON.stringify(next) !== JSON.stringify(selected)) {
        onChange(next);
      }
    }
  };

  const toggleId = (id: string) => {
    setInternal(prev => {
      const exists = prev.includes(id);
      const next = exists ? prev.filter(x => x !== id) : [...prev, id];
      pendingChangesRef.current = next;
      // Call immediate callback if provided
      onSelectionChange?.(next);
      return next;
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      applyPending();
    }
    setOpen(isOpen);
  };

  const selectAll = () => {
    const all = options.map(o => o.id);
    setInternal(all);
    pendingChangesRef.current = all;
    // Call immediate callback if provided
    onSelectionChange?.(all);
  };

  const clearAll = () => {
    setInternal([]);
    pendingChangesRef.current = [];
    // Call immediate callback if provided
    onSelectionChange?.([]);
  };

  const getLabel = () => {
    if (internal.length === 0) return placeholder;
    if (internal.length === 1) {
      const opt = options.find(o => o.id === internal[0]);
      return opt ? opt.label : placeholder;
    }
    return `${internal.length} loads selected`;
  };

  // Extract floor plan names for grouping if the data is available
  const hasFloorPlanData = options.some(opt => opt.floor_plan_id && opt.floor_plan_name);
  const shouldGroupByFloorPlan = hasFloorPlanData && currentFloorPlanId;

  // Extract all unique floor plan names for grouping
  const floorPlanNames = shouldGroupByFloorPlan
    ? [...new Set(options.filter(opt => opt.floor_plan_name).map(opt => opt.floor_plan_name))]
    : [];

  // Find current floor plan name
  const currentFloorPlanName = shouldGroupByFloorPlan
    ? floorPlanNames.find(name =>
        options.some(
          opt => opt.floor_plan_name === name && opt.floor_plan_id === currentFloorPlanId,
        ),
      )
    : null;

  // Other floor plan names
  const otherFloorPlanNames =
    shouldGroupByFloorPlan && currentFloorPlanName
      ? floorPlanNames.filter(name => name !== currentFloorPlanName)
      : [];

  const renderOption = (opt: Option, extraClass = '') => {
    const checked = internal.includes(opt.id);

    const handleToggle = () => {
      toggleId(opt.id);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    };

    return (
      <div
        key={opt.id}
        role="option"
        aria-selected={checked}
        tabIndex={0}
        className={cn(
          'flex items-center w-full rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          extraClass,
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <Checkbox checked={checked} className="mr-2 pointer-events-none" />
        {opt.label}
        {checked && <Check className="ml-auto h-4 w-4 text-primary" />}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className} aria-expanded={open}>
          {getLabel()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-72 bg-popover"
        align="start"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="border-b px-2 py-1 flex justify-between gap-2 text-xs">
          <button className="hover:underline" onClick={selectAll}>
            Select All
          </button>
          <button className="hover:underline" onClick={clearAll}>
            Clear All
          </button>
        </div>
        <ScrollArea className="h-64">
          <div className="p-2 space-y-1">
            {shouldGroupByFloorPlan ? (
              <>
                {/* Current floor plan circuits */}
                {currentFloorPlanName && (
                  <div className="mb-2">
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50 mb-1">
                      Current Floor Plan
                    </div>
                    {options
                      .filter(opt => opt.floor_plan_id === currentFloorPlanId)
                      .map(opt => renderOption(opt, 'mb-1'))}
                  </div>
                )}

                {/* Other floor plans */}
                {otherFloorPlanNames.map(floorPlanName => {
                  const floorPlanOptions = options.filter(
                    opt => opt.floor_plan_name === floorPlanName,
                  );
                  if (floorPlanOptions.length === 0) return null;

                  return (
                    <div key={floorPlanName} className="mb-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50 mb-1">
                        {floorPlanName}
                      </div>
                      {floorPlanOptions.map(opt => renderOption(opt, 'mb-1'))}
                    </div>
                  );
                })}

                {/* Options without floor plan data */}
                {options.filter(opt => !opt.floor_plan_id || !opt.floor_plan_name).length > 0 && (
                  <div>
                    {options
                      .filter(opt => !opt.floor_plan_id || !opt.floor_plan_name)
                      .map(opt => renderOption(opt, 'mb-1'))}
                  </div>
                )}
              </>
            ) : (
              // Original non-grouped layout
              options.map(opt => renderOption(opt))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
